import { useState } from "react";
import type { SupportItem } from "@/types/support";
import PencilIcon from "@/components/SVGComponents/PencilIcon";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";
import styles from "../Support.module.scss";

interface SupportCardProps {
  item: SupportItem;
  onEdit: (item: SupportItem) => void;
  onDelete: (item: SupportItem) => void;
  formatDate: (iso?: string) => string;
}

const SupportCard = ({ item, onEdit, onDelete, formatDate }: SupportCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLongAnswer = (item.answer?.length ?? 0) > 160;

  return (
    <div className={styles.mobileCard}>
      {/* Header: Question Badge, Title, and Action Buttons */}
      <div className={styles.mobileCardHeader}>
        <div className={styles.mobileQuestionBadge}>Q</div>
        <div className={styles.mobileQuestionContent}>
          <h3 className={styles.mobileQuestionText}>{item.question || "—"}</h3>
        </div>
        <div className={styles.mobileCardActions}>
          <button
            type="button"
            className={styles.mobileEditBtn}
            aria-label={`Edit "${item.question}"`}
            onClick={() => onEdit(item)}
          >
            <PencilIcon />
          </button>
          <button
            type="button"
            className={styles.mobileDeleteBtn}
            aria-label={`Delete "${item.question}"`}
            onClick={() => onDelete(item)}
          >
            <DeleteIcon width={24} height={24} />
          </button>
        </div>
      </div>

      {/* Answer Box */}
      <div className={styles.mobileAnswerBox}>
        <span className={styles.mobileAnswerLabel}>Answer</span>
        <p className={`${styles.mobileAnswerText} ${!isExpanded && isLongAnswer ? styles.clamped : ""}`}>
          {item.answer || "—"}
        </p>
        {isLongAnswer && (
          <button
            type="button"
            className={styles.mobileExpandBtn}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>

      {/* Meta Grid */}
      <div className={styles.mobileMetaGrid}>
        <div className={styles.mobileMetaCol}>
          <span className={styles.mobileMetaLabel}>Created on</span>
          <span className={styles.mobileMetaValue}>{formatDate(item.createdAt)}</span>
        </div>
        <div className={styles.mobileMetaCol}>
          <span className={styles.mobileMetaLabel}>Created by</span>
          <span className={styles.mobileMetaValue}>{item.createdByUser || "—"}</span>
        </div>
        <div className={styles.mobileMetaCol}>
          <span className={styles.mobileMetaLabel}>Updated on</span>
          <span className={styles.mobileMetaValue}>{formatDate(item.updatedAt)}</span>
        </div>
        <div className={styles.mobileMetaCol}>
          <span className={styles.mobileMetaLabel}>Updated by</span>
          <span className={styles.mobileMetaValue}>{item.updatedByUser || "—"}</span>
        </div>
      </div>
    </div>
  );
};

export default SupportCard;
